import { Component, Input, OnInit } from "@angular/core";
import * as Highcharts from "highcharts";
import { Router, ActivatedRoute } from "@angular/router";
import { find, orderBy } from "lodash";
import { KpiService } from "../../kpi/kpi.service";
import { OurStudentsHeaderMetadata } from "../../shared/interfaces";

@Component({
  selector: "stacked-column",
  templateUrl: "./stacked-column.component.html",
  styleUrls: ["./stacked-column.component.scss"],
})
export class StackedColumnComponent implements OnInit {
  options: Object;
  toolTipText: string;
  Highcharts = Highcharts;
  @Input()
  chartData;
  @Input()
  title;
  @Input()
  dataSuffix;
  @Input()
  dataPrefix;
  @Input()
  level;
  @Input()
  metricId;
  @Input()
  studentMetricId;
  @Input()
  entityId;

  studentKPIColumns;

  constructor(
    public kpiService: KpiService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.createChart();
    this.kpiService.getOurStudentsHeaderData().subscribe(
      (data: OurStudentsHeaderMetadata) => {
        this.studentKPIColumns = data.columns;
      },
      (error) => {
        console.error(error);
      }
    );
  }

  createChart() {
    var self = this;
    if (this.dataSuffix === "") {
      this.toolTipText =
        (this.dataPrefix || "") +
        "<strong>{point.numerator:.,f}</strong> students {series.options.definition}";
    } else {
      this.toolTipText =
        (this.dataPrefix || "") +
        "<strong>{point.numerator:.,f}</strong> of " +
        (this.dataPrefix || "") +
        "<strong>{point.denominator:.,f}</strong>" +
        " students {series.options.definition}";
    }

    this.options = {
      chart: {
        type: "column",
      },
      xAxis: {
        visible: false,
      },
      yAxis: {
        gridLineWidth: 0,
        min: 5,
        minRange: 0.1,
        max: 100,
        labels: {
          enabled: false,
        },
        title: {
          text: null,
        },
      },
      plotOptions: {
        column: {
          stacking: "percent",
          dataLabels: {
            enabled: true,
            color: "white",
            style: {
              textShadow: "0px",
              fontSize: "12px",
            },
            format: this.dataPrefix + "{point.y:.,f}" + this.dataSuffix,
          },
        },
        series: {
          cropThreshold: 3,
          pointWidth: 180,
          dataPrefix: this.dataPrefix,
          dataSuffix: this.dataSuffix,
          cursor: "pointer",
          point: {
            events: {
              click: function () {
                self.redirectToOurStudents(this.options);
              },
            },
          },
          events: {
            legendItemClick: function () {
              return false;
            },
          },
        },
      },
      title: { text: null },
      series: this.chartData,
      credits: false,
      legend: {
        symbolHeight: 12,
        symbolWidth: 12,
        symbolRadius: 6,
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
        itemMarginBottom: 2,
        width: 180,
        itemStyle: {
          width: 180,
          fontWeight: 400,
          fontSize: "1.1rem",
          fontFamily: "'Lato', Helvetica, sans-serif",
          color: "#6E6E6E",
          cursor: "default",
        },
        itemHoverStyle: {
          color: "#6e6e6e",
        },
        labelFormatter: function () {
          return (
            this.name +
            " (" +
            this.options.dataPrefix +
            Math.round(this.yData[0] * 10) / 10 +
            this.options.dataSuffix +
            ")"
          );
        },
      },
      tooltip: {
        shared: false,
        useHTML: true,
        borderColor: "#aaa",
        backgroundColor: "rgba(255, 255, 255, 1.0)",
        shadow: false,
        headerFormat: "",
        style: {
          fontFamily: "'Lato', Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        },
        pointFormat:
          '<div><span class="metric-tooltip title capitalize tile_body">{point.name}</span>' +
          '<span class="metric-tooltip value tooltip_value" style="color: {point.color};">' +
          this.dataPrefix +
          "{point.y:.,f}" +
          this.dataSuffix +
          '</span></div><div class="metric-tooltip description body_tooltip_text">' +
          this.toolTipText +
          "</div>",
      },
    };
  }

  //KPI-9385 - Added call to the force the chart to resize itself after a digest cycle.  This is needed in cases when the renderTo
  //container changes size, but the chart has already set its size.
  chartCallback = (chart) => {
    setTimeout(() => {
      if (chart.chartHeight !== chart.renderTo.clientHeight) {
        chart.setSize(undefined, null, true);
      }
    });
  };

  redirectToOurStudents(point) {
    if (!this.studentMetricId) return;
    const performanceBar = find(this.chartData, ["name", point.name]);
    let queryParams = { ...this.route.snapshot.queryParams };

    let adjustment = 0;
    const roundTo = find(this.studentKPIColumns, [
      "id",
      parseInt(this.studentMetricId),
    ]).roundTo;

    // Set adjustment to make PL max exclusive. For integers, adjustment = 1
    // For roundTo = 1 adjustment = 0.1
    // For roundTo = 2 adjustment = 0.01
    if (roundTo > 0) {
      adjustment = 1 / 10 ** roundTo;
    } else {
      adjustment = 1;
    }

    const pseudoUpIsGood = this.getPseudoUpIsGood();
    const numBars = this.chartData.length;
    const metricFilters = this.getMetricFilters(
      performanceBar,
      adjustment,
      numBars,
      pseudoUpIsGood
    );

    queryParams = {
      ...queryParams,
      metricFilters: metricFilters,
      metricSorts: `${this.studentMetricId},false`,
    };
    if (this.level === "school" && this.entityId) {
      queryParams = {
        ...queryParams,
        schoolId: this.entityId,
      };
    }

    this.router.navigate(["/comparison/student"], {
      relativeTo: this.route,
      queryParams,
    });
  }

  getMetricFilters(performanceBar, adjustment, numBars, pseudoUpIsGood) {
    if (
      performanceBar.performanceLevelMax - adjustment ==
      performanceBar.performanceLevelMin
    ) {
      // bar equals case
      return `${this.studentMetricId},${performanceBar.performanceLevelMin},${performanceBar.performanceLevelMin}`;
    } else {
      if (pseudoUpIsGood) {
        if (performanceBar.order == numBars) {
          return this.barGreaterThanCase(performanceBar.performanceLevelMin);
        } else if (performanceBar.order == 1) {
          return this.barLessThanCase(
            performanceBar.performanceLevelMax,
            adjustment
          );
        } else {
          return this.barBetweenCase(
            performanceBar.performanceLevelMin,
            performanceBar.performanceLevelMax,
            adjustment
          );
        }
      } else {
        if (performanceBar.order == numBars) {
          return this.barLessThanCase(
            performanceBar.performanceLevelMax,
            adjustment
          );
        } else if (performanceBar.order == 1) {
          return this.barGreaterThanCase(performanceBar.performanceLevelMin);
        } else {
          return this.barBetweenCase(
            performanceBar.performanceLevelMin,
            performanceBar.performanceLevelMax,
            adjustment
          );
        }
      }
    }
  }

  barGreaterThanCase(performanceLevelMin) {
    return `${this.studentMetricId},${performanceLevelMin},`;
  }

  barLessThanCase(performanceLevelMax, adjustment) {
    return `${this.studentMetricId},,${performanceLevelMax - adjustment}`;
  }

  barBetweenCase(performanceLevelMin, performanceLevelMax, adjustment) {
    return `${this.studentMetricId},${performanceLevelMin},${
      performanceLevelMax - adjustment
    }`;
  }

  getPseudoUpIsGood() {
    const orderedChartData = orderBy(this.chartData, ["order"], ["asc"]);
    if (orderedChartData && orderedChartData.length > 1) {
      // we look at the last bar's performance level max - the first bar's performance level max to see if up is good or not
      // (we can't use the metric's upIsGood because it isn't stored for the student metric, and this causes weird issues for Attendance/Chronic Absence)
      return (
        orderedChartData[orderedChartData.length - 1].performanceLevelMax -
          orderedChartData[0].performanceLevelMax >
        0
      );
    }
  }
}
